/**
 * HoursDB — the "Hours" master database on the Search page. A weekly read
 * view straight from the live time-clock data (no import, no scan): one row
 * per week with the date range, total hours worked and how many employees
 * worked. Expanding a week lists every worked day, and each day lists every
 * employee with their hours and the job location(s) they punched into.
 * A week can be deleted (removes the real timesheets + punches — the
 * confirmation popup spells that out).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Minus, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";

const fmtHours = (h) => `${Number(h || 0).toLocaleString("en-CA", { maximumFractionDigits: 2 })}h`;

const fmtDay = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
};

const fmtRange = (start, end) => {
  try {
    const s = new Date(`${start}T12:00:00`);
    const e = new Date(`${end}T12:00:00`);
    const opts = { month: "short", day: "numeric" };
    return `${s.toLocaleDateString("en-CA", opts)} – ${e.toLocaleDateString("en-CA", { ...opts, year: "numeric" })}`;
  } catch { return `${start} – ${end}`; }
};

export default function HoursDB() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [detail, setDetail] = useState({});          // week_start -> detail payload
  const [detailLoading, setDetailLoading] = useState(new Set());
  const [confirmDel, setConfirmDel] = useState(null); // week object
  const [deleting, setDeleting] = useState(false);

  const load = async (silent = true) => {
    try {
      const { data } = await api.get("/master/hours/weeks");
      setWeeks(data);
    } catch {
      if (!silent) toast.error("Could not load the hours database");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, []);

  const toggle = async (ws) => {
    const next = new Set(expanded);
    if (next.has(ws)) {
      next.delete(ws);
      setExpanded(next);
      return;
    }
    next.add(ws);
    setExpanded(next);
    if (!detail[ws]) {
      setDetailLoading((s) => new Set(s).add(ws));
      try {
        const { data } = await api.get(`/master/hours/weeks/${ws}`);
        setDetail((d) => ({ ...d, [ws]: data }));
      } catch {
        toast.error("Could not load that week");
        setExpanded((s) => { const n = new Set(s); n.delete(ws); return n; });
      } finally {
        setDetailLoading((s) => { const n = new Set(s); n.delete(ws); return n; });
      }
    }
  };

  const deleteWeek = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/master/hours/weeks/${confirmDel.week_start}`);
      toast.success(`Week deleted — ${data.deleted_timesheets} timesheets, ${data.deleted_punches} punches removed`);
      setConfirmDel(null);
      setExpanded((s) => { const n = new Set(s); n.delete(confirmDel.week_start); return n; });
      setDetail((d) => { const n = { ...d }; delete n[confirmDel.week_start]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete that week");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="master-db-hours">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          Hours · master database · every period from the time clock
        </p>
      </div>

      {loading && <p className="text-xs text-white/40" data-testid="hours-loading">Loading…</p>}

      <section>
        <h2 className="mb-2 font-mono text-xs font-semibold uppercase tracking-widest text-white/70" data-testid="hours-heading">
          Weekly hours
          <span className="ml-2 text-white/35">{weeks.length}</span>
        </h2>

        {weeks.length === 0 && !loading && (
          <p className="rounded-lg border border-white/10 px-3 py-8 text-center text-xs text-white/35" data-testid="hours-empty">
            No hours logged yet — they appear here automatically once employees start clocking in.
          </p>
        )}

        {weeks.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full min-w-[560px] border-collapse" data-testid="hours-table">
              <thead>
                <tr className="bg-white/5">
                  <th className="w-10 px-2.5 py-1.5"></th>
                  <th className="px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest text-white/50">Week</th>
                  <th className="px-2.5 py-1.5 text-right font-mono text-[9px] uppercase tracking-widest text-white/50">Total hours</th>
                  <th className="px-2.5 py-1.5 text-right font-mono text-[9px] uppercase tracking-widest text-white/50">Employees</th>
                  <th className="w-12 px-2.5 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => [
                  <tr key={w.week_start} className="border-t border-white/5 hover:bg-white/[0.03]"
                    data-testid={`hours-week-${w.week_start}`}>
                    <td className="px-2.5 py-1.5">
                      <button type="button" onClick={() => toggle(w.week_start)}
                        data-testid={`hours-expand-${w.week_start}`}
                        aria-label={expanded.has(w.week_start) ? "Collapse week" : "Expand week"}
                        className="rounded border border-white/15 p-1 text-white/60 hover:border-white/30 hover:text-white">
                        {expanded.has(w.week_start) ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      </button>
                    </td>
                    <td className="px-2.5 py-1.5 text-xs font-semibold text-white" data-testid={`hours-range-${w.week_start}`}>
                      {fmtRange(w.week_start, w.week_end)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right text-xs font-semibold text-[#fb923c]" data-testid={`hours-total-${w.week_start}`}>
                      {fmtHours(w.total_hours)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right text-xs text-white/70" data-testid={`hours-count-${w.week_start}`}>
                      {w.employee_count}
                    </td>
                    <td className="px-2.5 py-1.5 text-right">
                      <button type="button" onClick={() => setConfirmDel(w)}
                        data-testid={`hours-del-${w.week_start}`} aria-label="Delete week"
                        className="rounded p-1 text-white/25 hover:bg-red-500/15 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>,
                  expanded.has(w.week_start) && (
                    <tr key={`${w.week_start}-detail`} className="border-t border-white/5 bg-white/[0.02]">
                      <td colSpan={5} className="px-4 py-3" data-testid={`hours-detail-${w.week_start}`}>
                        {detailLoading.has(w.week_start) && (
                          <p className="flex items-center gap-2 text-xs text-white/40"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading days…</p>
                        )}
                        {detail[w.week_start] && detail[w.week_start].days.length === 0 && (
                          <p className="text-xs text-white/35">No worked days in this week.</p>
                        )}
                        {detail[w.week_start] && detail[w.week_start].days.map((d) => (
                          <div key={d.date} className="mb-3 last:mb-0" data-testid={`hours-day-${d.date}`}>
                            <div className="mb-1 flex items-baseline justify-between">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-white/60">{fmtDay(d.date)}</p>
                              <p className="font-mono text-[10px] text-white/35">{fmtHours(d.total_hours)}</p>
                            </div>
                            <div className="space-y-1">
                              {d.employees.map((emp) => (
                                <div key={emp.employee_id} className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.03] px-2.5 py-1.5"
                                  data-testid={`hours-emp-${d.date}-${emp.employee_id}`}>
                                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-white/85">{emp.name}</span>
                                  <span className="shrink-0 text-xs font-semibold text-[#fb923c]">{fmtHours(emp.hours)}</span>
                                  <span className="min-w-0 flex-1 truncate text-right text-[11px] text-white/45" title={(emp.locations || []).join(", ")}>
                                    {(emp.locations || []).length ? emp.locations.join(", ") : "no job noted"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ),
                ])}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* delete week confirmation popup */}
      {confirmDel && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4"
          onClick={() => !deleting && setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="hours-del-confirm-dialog">
            <h3 className="text-sm font-bold text-white">
              Delete the week of {fmtRange(confirmDel.week_start, confirmDel.week_end)}?
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-white/50">
              This permanently deletes <b className="text-red-300">{fmtHours(confirmDel.total_hours)}</b> of real time-clock data for{" "}
              {confirmDel.employee_count} employee{confirmDel.employee_count === 1 ? "" : "s"} — the week's timesheets AND the underlying
              punches. Payroll and vacation accruals will recompute without them. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDel(null)} disabled={deleting}
                data-testid="hours-del-cancel"
                className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white">
                Cancel
              </button>
              <button type="button" onClick={deleteWeek} disabled={deleting}
                data-testid="hours-del-confirm"
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
